import React, { useState, useEffect } from 'react';
import { Tooltip } from 'reactstrap';
import { type IFavourite, type IDeleteFavoriteRequestPayload } from '../../redux/types/builder';
import { useAppDispatch } from '../../redux/hooks';
import { deleteFavourite, getFavourites } from '../../redux/actions/customizer';
import { isFavouriteUrlMatch } from '../../utils/favourites';
import AuthManager from '../../utils/AuthManager';
import axiosClient from '../../redux/services/apiClient';
import { APP_STYLES, DEFAULT_STYLES } from '../../utils/basic';
import './index.scss';
import { sendAnalytics } from '../../utils/analytics';

const NO_IMAGE_URL =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="180" height="180" fill="%23ccc"%3E%3Crect width="180" height="180" fill="%23f5f5f5"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-size="14" fill="%23999"%3ENo Image%3C/text%3E%3C/svg%3E';

const formatCurrency = (price: string): string => {
  if (!price) return '';
  const n = parseFloat(price.replace(/[^\d.-]/g, ''));
  if (!Number.isFinite(n)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
};

const formatSalePrice = (price: string, discount: number): string => {
  if (!price) return '';
  const n = parseFloat(price.replace(/[^\d.-]/g, ''));
  if (!Number.isFinite(n)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n * (1 - discount / 100));
};

interface IProps {
  favourites: IFavourite[];
  currentProductUrl?: string;
  onNavigate: (url: string) => void;
  onCurrentToggle?: () => void;
  subpath?: string;
  currencyCode?: string;
  saleDiscount?: number;
}

const BuilderFavourites = ({
  favourites,
  currentProductUrl,
  onNavigate,
  onCurrentToggle,
  subpath,
  currencyCode = 'USD',
  saleDiscount = 0
}: IProps) => {
  const dispatch = useAppDispatch();
  const [confirmClear, setConfirmClear] = useState(false);
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [loadingPrices, setLoadingPrices] = useState<Set<string>>(new Set());
  const [openRemoveTooltip, setOpenRemoveTooltip] = useState<number | null>(null);

  useEffect(() => {
    if (!favourites.length) return;

    const userId = AuthManager.getAnonymousUserId();

    setPrices({});
    setLoadingPrices(new Set(favourites.map((fav) => fav.product_url)));

    const headers: Record<string, string> = {};
    if (subpath) headers['subpath'] = subpath;
    if (currencyCode) headers['currency-code'] = currencyCode;

    favourites.forEach(async (fav) => {
      try {
        let price = '';
        const version = fav.product_url.startsWith('/women/') || !fav.product_url.includes('/profiles/') ? 'v2' : 'v1';
        const separator = fav.product_url.includes('?') ? '&' : '?';
        const userSuffix = userId ? `${separator}user_id=${userId}` : '';
        const apiUrl = `/${version}${fav.product_url}${userSuffix}`;
        const res = await axiosClient.get(apiUrl, { headers });
        price = res.data?.price ?? '';
        setPrices((prev) => ({ ...prev, [fav.product_url]: price }));
      } catch (err) {
        console.error('Failed to fetch favourite price:', fav.product_url, err);
        setPrices((prev) => ({ ...prev, [fav.product_url]: '' }));
      } finally {
        setLoadingPrices((prev) => {
          const next = new Set(prev);
          next.delete(fav.product_url);
          return next;
        });
      }
    });
  }, [favourites, subpath, currencyCode]);

  const isCurrentProduct = (productUrl: string) => {
    return currentProductUrl && isFavouriteUrlMatch(currentProductUrl, productUrl);
  };

  const handleRemove = (e: React.MouseEvent, productUrl: string) => {
    e.stopPropagation();
    sendAnalytics('favorites', 'Remove Favorite - Builder Favorites', subpath);
    const data: IDeleteFavoriteRequestPayload = { product_url: productUrl };
    dispatch(deleteFavourite(data)).then(() => {
      if (isCurrentProduct(productUrl) && onCurrentToggle) {
        onCurrentToggle();
      }
      dispatch(getFavourites());
    });
  };

  const handleClearAll = () => {
    if (!confirmClear) {
      setConfirmClear(true);
      return;
    }
    setConfirmClear(false);
    sendAnalytics('favorites', 'Clear All - Builder', subpath);
    const currentIsFavorited = favourites.some((fav) => isCurrentProduct(fav.product_url));
    Promise.all(
      favourites.map((fav) => {
        const data: IDeleteFavoriteRequestPayload = { product_url: fav.product_url };
        return dispatch(deleteFavourite(data));
      })
    ).then(() => {
      if (currentIsFavorited && onCurrentToggle) {
        onCurrentToggle();
      }
      dispatch(getFavourites());
    });
  };

  const handleCardClick = (productUrl: string) => {
    onNavigate(productUrl);
  };

  const getImageSrc = (ringImageUrl: string) => {
    if (!ringImageUrl) return NO_IMAGE_URL;
    let imgUrl = ringImageUrl;
    const styles = APP_STYLES[subpath || ''] || DEFAULT_STYLES;
    imgUrl = imgUrl.replace('&watermarkBrand=emblem', '');
    if (!styles.watermarkBrand) {
      imgUrl += (imgUrl.includes('?') ? '&' : '?') + 'watermark=false';
    } else {
      imgUrl += (imgUrl.includes('?') ? '&' : '?') + `watermarkBrand=${styles.watermarkBrand}`;
    }
    const src = `${process.env.REACT_APP_ASSET_URL}${imgUrl}`;
    return `${src}&image_size=180`;
  };

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    (e.target as HTMLImageElement).src = NO_IMAGE_URL;
  };

  if (!favourites.length) return null;

  return (
    <div className="builder-favourites swatch-filter-border">
      <div className="builder-favourites__header">
        {confirmClear ? (
          <>
            <span className="builder-favourites__confirm-text">Clear all saved designs?</span>
            <button
              type="button"
              className="builder-favourites__confirm-btn builder-favourites__confirm-btn--yes"
              onClick={handleClearAll}
            >
              Yes
            </button>
            <button
              type="button"
              className="builder-favourites__confirm-btn builder-favourites__confirm-btn--no"
              onClick={() => setConfirmClear(false)}
            >
              No
            </button>
          </>
        ) : (
          <button type="button" className="builder-favourites__clear-btn" onClick={handleClearAll}>
            <i className="fa fa-times" aria-hidden /> Clear All
          </button>
        )}
      </div>
      <div className="builder-favourites__list">
        {favourites.map((fav) => (
          <div
            key={fav.favorite_id}
            className={`builder-favourites__card${isCurrentProduct(fav.product_url) ? ' builder-favourites__card--active' : ''}`}
            onClick={() => handleCardClick(fav.product_url)}
          >
            <button
              id={`remove-fav-${fav.favorite_id}`}
              type="button"
              className="builder-favourites__remove-btn"
              onClick={(e) => handleRemove(e, fav.product_url)}
              aria-label="Remove Design"
            >
              <i className="fa fa-times" aria-hidden />
            </button>
            <Tooltip
              placement="top"
              isOpen={openRemoveTooltip === fav.favorite_id}
              target={`remove-fav-${fav.favorite_id}`}
              toggle={() => {
                setOpenRemoveTooltip(openRemoveTooltip === fav.favorite_id ? null : fav.favorite_id);
              }}
              className="fav-tooltip"
            >
              Remove Design
            </Tooltip>
            <img
              src={getImageSrc(fav.ring_image_url)}
              alt="Favorite ring"
              className="builder-favourites__image"
              loading="lazy"
              onError={handleImageError}
            />
            <div className="builder-favourites__price">
              {loadingPrices.has(fav.product_url) ? (
                <i className="fa fa-spinner fa-spin" aria-hidden />
              ) : saleDiscount > 0 && prices[fav.product_url] ? (
                <>
                  <span>{formatSalePrice(prices[fav.product_url], saleDiscount)}</span>{' '}
                  <span className="tw-line-through tw-text-gray-500">{formatCurrency(prices[fav.product_url])}</span>
                </>
              ) : (
                prices[fav.product_url] || ''
              )}
            </div>
            <button
              type="button"
              className="builder-favourites__action-btn !tw-bg-brand-color !tw-text-white"
              onClick={(e) => {
                e.stopPropagation();
                handleCardClick(fav.product_url);
              }}
            >
              {fav.product_url.includes('static-product') ? 'View' : 'Customize'}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BuilderFavourites;
